import { ORPCError } from "@orpc/client";
import {
  type NoteGenerationBody,
  noteGenerationSchema,
  visitLanguageSchema,
} from "@rosc/contracts";
import { t } from "i18next";
import { z } from "zod";
import type {
  CreateRecordingRequestBody,
  CreateRecordingResponseData,
  CreateVisitRequestBody,
  CreateVisitResponseData,
} from "@/common/types/visits";
import { referenceClient } from "@/lib/orpc/client";
import { withRequestTimeout } from "@/lib/upload-watchdog";

/**
 * The API rejected the visit input (HTTP 400, e.g. oRPC "Input validation
 * failed"). Validation runs before anything is written, so nothing was saved
 * and the submission key can be released.
 */
export class VisitRejectedError extends Error {
  constructor() {
    super(t("visits.submission.rejected"));
    this.name = "VisitRejectedError";
  }
}

/**
 * The recorder's language codes ("cmn", "jap", "auto") as the contract's
 * stored codes ("zho", "jpn", "und"). An unknown value is sent unchanged so
 * the API reports it.
 */
export const contractLanguage = (language: string): string => {
  const parsed = visitLanguageSchema.safeParse(language);
  return parsed.success ? parsed.data : language;
};

export const createVisit = async (
  body: CreateVisitRequestBody,
  identity: { key: string; userUid: string },
): Promise<CreateVisitResponseData> => {
  const fields = [
    "name",
    "title",
    "duration",
    "style",
    "format",
    "exam",
    "template",
    "language",
    "transcription",
    "customTemplatePublicId",
    "instructions",
    "skipShortcuts",
    "recordingConsent",
  ];
  if (Object.keys(body).some((key) => !fields.includes(key))) {
    throw new Error("Unexpected visit field");
  }
  const request = { ...body, language: contractLanguage(body.language) };
  // Bounded so a dropped connection cannot leave the dialog processing forever.
  // The request itself is not aborted; the idempotency key makes a retry after
  // a timeout safe even if the first request later succeeds.
  const response = await withRequestTimeout(() =>
    referenceClient.createVisit(
      { body: request, headers: { "idempotency-key": identity.key } },
      { context: { expectedUserUid: identity.userUid } },
    ),
  ).catch((error: unknown) => {
    if (error instanceof ORPCError && error.code === "BAD_REQUEST")
      throw new VisitRejectedError();
    throw error;
  });
  const { public_id } = z
    .object({ public_id: z.uuidv4().toLowerCase() })
    .strict()
    .parse(response);
  return { visitPublicId: public_id };
};

export const createRecording = async (
  body: CreateRecordingRequestBody,
  identity: { key: string; userUid: string },
): Promise<CreateRecordingResponseData> => {
  const fields = [
    "visitPublicId",
    "name",
    "duration",
    "language",
    "transcription",
    "recordingConsent",
  ];
  if (Object.keys(body).some((key) => !fields.includes(key)))
    throw new Error("Unexpected recording field");
  const { visitPublicId, ...recording } = body;
  const response = await referenceClient.createRecording(
    {
      params: { public_id: String(visitPublicId) },
      body: { ...recording, language: contractLanguage(recording.language) },
      headers: { "idempotency-key": identity.key },
    },
    { context: { expectedUserUid: identity.userUid } },
  );
  const { public_id } = z
    .object({ public_id: z.uuidv4().toLowerCase() })
    .strict()
    .parse(response);
  return { recordingPublicId: public_id };
};

export const createVisitNote = async (
  visitPublicId: string,
  body: NoteGenerationBody,
  identity: { key: string; userUid: string },
): Promise<{ public_id: string }> => {
  const parsed = noteGenerationSchema.parse(body);
  const response = await referenceClient.createVisitNote(
    {
      params: { public_id: String(visitPublicId) },
      body: parsed,
      headers: { "idempotency-key": identity.key },
    },
    { context: { expectedUserUid: identity.userUid } },
  );
  return z
    .strictObject({ public_id: z.uuidv4().toLowerCase() })
    .parse(response);
};
